import type { CaseForPharmacy } from "@omony/domain";
import { Button } from "@omony/ui";
import { House, Receipt } from "lucide-react";
import { navigate } from "../../shared/hashRoute.js";
import { PatientLanguage } from "../PatientLanguage.js";
import { Spacer, StepFrame, useT } from "./common.js";
import styles from "./flow.module.css";

/** Notfall bzw. „nicht geeignet“ (A14): unmissverständlich, in beiden Sprachen */
export function TriageOutcome({ c, onBilling }: { c: CaseForPharmacy; onBilling: () => void }) {
  const t = useT();
  const notfall = c.cancelReason === "notfall";
  const reasons = c.triage?.reasons ?? [];
  return (
    <StepFrame
      eyebrow={t("console.step.ersteinschaetzung")}
      title={t(notfall ? "console.outcome.notfallTitle" : "console.outcome.ungeeignetTitle")}
      actions={
        <>
          <Spacer />
          {c.billing.status === "offen" ? (
            <Button size="lg" icon={<Receipt />} onPress={onBilling}>
              {t("console.outcome.toBilling")}
            </Button>
          ) : (
            <Button size="lg" icon={<House />} onPress={() => navigate("")}>
              {t("console.done.home")}
            </Button>
          )}
        </>
      }
    >
      <div
        className={`${styles.outcome} ${notfall ? styles.outcomeDanger : styles.outcomeWarning}`}
        role="alert"
      >
        <p>{t(notfall ? "console.outcome.notfall" : "console.outcome.ungeeignet")}</p>
        {reasons.length ? (
          <p className={styles.small}>
            {t("console.outcome.reasons", {
              list: reasons
                .map((r) => t(notfall ? `patient.bogen.flag.${r}` : `console.outcome.reason.${r}`))
                .join(", "),
            })}
          </p>
        ) : null}
      </div>
      <PatientLanguage locale={c.patientLanguage}>
        <PatientOutcome notfall={notfall} />
      </PatientLanguage>
    </StepFrame>
  );
}

function PatientOutcome({ notfall }: { notfall: boolean }) {
  const t = useT();
  return (
    <div className={`${styles.outcome} ${notfall ? styles.outcomeDanger : styles.outcomeWarning}`}>
      <p className={styles.outcomeTitle}>
        {t(notfall ? "patient.outcome.notfallTitle" : "patient.outcome.ungeeignetTitle")}
      </p>
      {notfall ? <p className={styles.emergency}>{t("patient.outcome.emergencyNumber")}</p> : null}
      <p>{t(notfall ? "patient.outcome.notfall" : "patient.outcome.ungeeignet")}</p>
    </div>
  );
}

/** Abgebrochener Vorgang ohne offene Abrechnung */
export function CancelledStep({ c }: { c: CaseForPharmacy }) {
  const t = useT();
  return (
    <StepFrame
      title={t("console.cancelled.title")}
      lead={t("console.cancelled.lead", {
        reason: c.cancelReason ? t(`case.cancel.${c.cancelReason}`) : t("console.cancelled.unknown"),
        by: t(`console.cancelled.by.${c.cancelledByRole ?? "system"}`),
      })}
      actions={
        <>
          <Spacer />
          <Button size="lg" icon={<House />} onPress={() => navigate("")}>
            {t("console.done.home")}
          </Button>
        </>
      }
    />
  );
}
